import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCancelBulkProcess,
  useGetBulkUploadStatusById,
} from "../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import {
  BulkInsertHeader,
  ITabs,
  MuiButtonSecondary,
  MuiTabPanel,
  MuiTabs,
} from "../../components";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
  SummaryStatus,
} from "../../helpers/constants";
import { BulkCreateProgramWrapper } from "./BulkCreateProgram.style";
import { BulkCreateProgramStepper } from "./components/bulkCreateProgramStepper/BulkCreateProgramStepper";
import { BulkProgramDownloadSmartTemplate } from "./components/bulkProgramDownloadSmartTemplate/BulkProgramDownloadSmartTemplate";
import { BulkCreateProgramProcess } from "./components/steps/bulkCreateProgramProcess/BulkCreateProgramProcess";
import { BulkCreateProgramSummary } from "./components/steps/bulkCreateProgramSummary/BulkCreateProgramSummary";

import { saveAs } from "file-saver";
import { useDispatch } from "react-redux";
import { useFileDownloadMutation } from "../../api/services/client/clientApis";
import { BulkInsertFooterWrapper } from "../../components/bulkInsert/bulkInsertFooter/BulkInsertFooter.style";
import { extractFileName } from "../../helpers/utils";
import { usePermissions } from "../../hooks/usePermissions";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { progressStateComponentSiteCoreKeys } from "../manageBulkProcess/siteCore/SiteCoreKeys";
import { BulkCreateProgramUpload } from "./components/steps/bulkCreateProgramUpload/BulkCreateProgramUpload";
import {
  bulkDocumentType,
  BulkUploadStatusResponse,
  PROGRAM_FLOW_MODES,
  ProgramFlowMode,
} from "./modelsAndSchema/Models";

const initialSummaryData: BulkUploadStatusResponse = {
  totalRecords: 0,
  processedRecords: 0,
  unProcessRecords: 0,
  status: "",
  bulkImportId: "",
  processName: "",
};

interface BulkCreateProgramProps {
  isAddViewOnly?: boolean;
  isEditBulkProgram?: boolean;
  pageId?: string;
  bulkImportId?: string;
  status?: string;
  stepValue?: number;
  setEditCurrentStep?: (step: number) => void;
  isEditViewOnly?: boolean;
}

const BulkCreateProgram: React.FC<BulkCreateProgramProps> = ({
  isAddViewOnly = false,
  isEditBulkProgram = false,
  pageId = PAGE_IDS.CREATE_BULK_TRANSFER,
  bulkImportId,
  status,
  stepValue = 0,
  setEditCurrentStep,
  isEditViewOnly = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { hasModulePermission } = usePermissions();

  const [refreshCount, setRefreshCount] = useState(0);
  const [currentStep, setCurrentStep] = useState(stepValue);
  const [showDownloadTemplateModal, setShowDownloadTemplateModal] =
    useState(false);
  const [summaryData, setSummaryData] =
    useState<BulkUploadStatusResponse>(initialSummaryData);
  const [isUploadProceed, setIsUploadProceed] = useState(false);

  const { commonLabels } = useSiteCore();
  const location = useLocation();
  const fromPage = location.state?.from ?? null;

  const {
    isPending: isFeedbackPending,
    data: feedbackData,
    isError: isFeedbackDataError,
    isSuccess: isFeedbackSuccess,
    refetchWithParams: refetchFeedbackData,
  } = useGetBulkUploadStatusById();

  const {
    mutate: downloadFile,
    isError: isErrorOnDownload,
    isPending: isPendingOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  const {
    mutateAsync: cancelBulkProcessMutate,
    isPending: isCancelProcessPending,
    isSuccess: isCancelProcessSuccess,
    isError: isCancelProcessError,
  } = useCancelBulkProcess();

  const tabIdOne = `${pageId}-tab1`;
  const tabIdTwo = `${pageId}-tab2`;
  const tabIdThree = `${pageId}-tab3`;
  const tabs: ITabs[] = [
    {
      id: tabIdOne,
      label: "",
    },
    {
      id: tabIdTwo,
      label: "",
    },
    {
      id: tabIdThree,
      label: "",
    },
  ];

  const handleBulkUploadSuccess = (data: BulkUploadStatusResponse) => {
    setSummaryData(data);
    setCurrentStep((prev) => prev + 1);
  };

  const getProgramFlowMode = (): ProgramFlowMode => {
    if (isAddViewOnly) return PROGRAM_FLOW_MODES.VIEW;
    if (isEditBulkProgram) return PROGRAM_FLOW_MODES.EDIT;
    return PROGRAM_FLOW_MODES.CREATE;
  };

  const onCreateNewBulkProgram = () => {
    switch (getProgramFlowMode()) {
      case PROGRAM_FLOW_MODES.VIEW:
        checkPermissionAndNavigate(
          MODULE_PERMISSIONS.BULK_CREATE,
          PAGE_URLS.BULK_CREATE_PROGRAM,
        );
        break;

      case PROGRAM_FLOW_MODES.EDIT:
        if (isEditViewOnly) {
          checkPermissionAndNavigate(
            MODULE_PERMISSIONS.BULK_EDIT,
            PAGE_URLS.BULK_EDIT_PROGRAM,
          );
        } else {
          setEditCurrentStep?.(0);
        }
        break;

      case PROGRAM_FLOW_MODES.CREATE:
      default:
        setSummaryData(initialSummaryData);
        setCurrentStep(0);
    }
  };

  const onHandleNavigateBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (isFeedbackDataError || isCancelProcessError || isErrorOnDownload) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isFeedbackDataError, isErrorOnDownload, isCancelProcessError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          (isFeedbackPending && currentStep === 1) ||
          isPendingOnDownload ||
          isCancelProcessPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFeedbackPending, isPendingOnDownload, isCancelProcessPending]);

  useEffect(() => {
    if (isSuccessOnDownload) {
      let fileName = "";
      if (dataOnDownload.contentDisposition) {
        fileName = extractFileName(dataOnDownload.contentDisposition);
      }
      saveAs(dataOnDownload.blob, `${fileName}`);
    }
  }, [isSuccessOnDownload]);

  useEffect(() => {
    if (isFeedbackSuccess) {
      setSummaryData((prev) => ({
        ...prev,
        ...feedbackData,
      }));
    }
  }, [isFeedbackSuccess, feedbackData]);

  const checkPermissionAndNavigate = (permission: string, url: string) => {
    if (hasModulePermission(permission)) {
      navigate(url);
    } else {
      navigate(PAGE_URLS.DASHBOARD);
    }
  };

  useEffect(() => {
    if (isCancelProcessSuccess) {
      if (isAddViewOnly) {
        checkPermissionAndNavigate(
          MODULE_PERMISSIONS.BULK_PROCESS_STATS,
          PAGE_URLS.MANAGE_BULK_PROCESS,
        );
      } else {
        navigate(PAGE_URLS.SEARCH_PROGRAM);
      }
    }
  }, [isCancelProcessSuccess]);

  useEffect(() => {
    if (isFeedbackSuccess && summaryData.status === SummaryStatus.COMPLETED) {
      setCurrentStep(2);
    }
  }, [isFeedbackSuccess, summaryData?.status]);

  useEffect(() => {
    if (
      (currentStep === 1 && summaryData.bulkImportId) ||
      (currentStep === 2 && isAddViewOnly) ||
      isEditViewOnly
    ) {
      refetchFeedbackData({ bulkImportId: summaryData.bulkImportId });
    }
  }, [summaryData?.bulkImportId, currentStep, refreshCount]);

  const handleRefresh = () => {
    setRefreshCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (isAddViewOnly || isEditViewOnly) {
      setSummaryData((prev) => ({
        ...prev,
        status: status ?? "",
        bulkImportId: bulkImportId ?? "",
      }));
    }
  }, [isAddViewOnly, isEditViewOnly]);

  const handleCancelProcess = () => {
    const params = {
      action: progressStateComponentSiteCoreKeys.cancelledState,
      bulkImportId: summaryData.bulkImportId,
    };
    cancelBulkProcessMutate({ params });
  };

  const handleDownloadUploadedFile = () => {
    if (summaryData?.documentId) {
      downloadFile({
        documentId: summaryData?.documentId,
        documentType: bulkDocumentType,
      });
    }
  };
  const onHandleCancel = () => {
    if (isEditBulkProgram) {
      setEditCurrentStep?.(0);
    } else if (fromPage === PAGE_URLS.SEARCH_PROGRAM) {
      navigate(PAGE_URLS.SEARCH_PROGRAM);
    } else {
      navigate(PAGE_URLS.DASHBOARD);
    }
  };

  return (
    <BulkCreateProgramWrapper>
      {!isEditBulkProgram && (
        <BulkInsertHeader
          isShowDownloadButton
          currentStep={currentStep}
          onDownloadClick={() => setShowDownloadTemplateModal(true)}
        />
      )}
      <BulkCreateProgramStepper
        currentStep={currentStep}
        isUploadProceed={isUploadProceed}
        status={summaryData.status}
        isAllProceed={
          summaryData.status === SummaryStatus.COMPLETED &&
          summaryData.unProcessRecords === 0
        }
        isEditBulkProgram={isEditBulkProgram}
      />
      <MuiTabs
        tabs={tabs}
        value={currentStep}
        tabsTestIds={[tabIdOne, tabIdTwo, tabIdThree]}
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={currentStep}
          index={0}
          isPanelWithMount
          ariaLabelledById={tabIdOne}
        >
          <BulkCreateProgramUpload
            pageId={pageId}
            onBulkUploadSuccess={handleBulkUploadSuccess}
            handleNavigateBack={onHandleCancel}
            onUploadReadyStateChange={setIsUploadProceed}
            isEditBulkProgram={isEditBulkProgram}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={currentStep}
          index={1}
          isPanelWithMount
          ariaLabelledById={tabIdTwo}
        >
          <BulkCreateProgramProcess
            onCreateNewBulkProgram={onCreateNewBulkProgram}
            onRefresh={handleRefresh}
            summaryData={summaryData}
            onCancelProcess={handleCancelProcess}
            onDownloadUploadedFile={handleDownloadUploadedFile}
            isEditBulkProgram={isEditBulkProgram}
          />
        </MuiTabPanel>

        <MuiTabPanel
          panelId={`${pageId}-panel3`}
          value={currentStep}
          index={2}
          isPanelWithMount
          ariaLabelledById={tabIdThree}
        >
          <BulkCreateProgramSummary
            summaryData={summaryData}
            onCreateNewBulkProgram={onCreateNewBulkProgram}
            pageId={pageId}
            isEditBulkProgram={isEditBulkProgram}
          />
        </MuiTabPanel>
      </MuiTabs>

      {(isAddViewOnly || isEditViewOnly) && (
        <BulkInsertFooterWrapper className="footer-close-btn">
          <MuiButtonSecondary
            id={`${pageId}-close`}
            onClick={onHandleNavigateBack}
          >
            {commonLabels.close}
          </MuiButtonSecondary>
        </BulkInsertFooterWrapper>
      )}

      {showDownloadTemplateModal && (
        <BulkProgramDownloadSmartTemplate
          pageId={pageId}
          open={showDownloadTemplateModal}
          onClose={() => setShowDownloadTemplateModal(false)}
        />
      )}
    </BulkCreateProgramWrapper>
  );
};

export default BulkCreateProgram;
